
import { format } from 'date-fns';
import { supabase } from '@/integrations/supabase/client';

interface CertificateData {
  studentName: string;
  courseName: string;
  completionDate: Date;
  certificateNumber: string;
  citationNumber?: string;
  county?: string;
  driverLicense?: string;
  dob?: Date;
  courseReason?: string;
}

/**
 * Generates a certificate HTML by replacing placeholders in a template with actual data
 */
export function generateCertificateHtml(templateHtml: string, data: CertificateData): string {
  let html = templateHtml;
  
  // Format dates
  const formattedCompletionDate = format(data.completionDate, 'MMMM d, yyyy');
  const formattedDob = data.dob ? format(data.dob, 'MMMM d, yyyy') : '';
  
  // Replace placeholders with actual data
  html = html.replace(/{{student_name}}/g, data.studentName || '');
  html = html.replace(/{{course_name}}/g, data.courseName || '');
  html = html.replace(/{{completion_date}}/g, formattedCompletionDate);
  html = html.replace(/{{certificate_number}}/g, data.certificateNumber || '');
  html = html.replace(/{{citation_number}}/g, data.citationNumber || 'Not provided');
  html = html.replace(/{{county}}/g, data.county || 'Not provided');
  html = html.replace(/{{driver_license}}/g, data.driverLicense || 'Not provided');
  html = html.replace(/{{dob}}/g, formattedDob || 'Not provided');
  html = html.replace(/{{course_reason}}/g, data.courseReason || 'Not specified');
  
  return html;
}

/**
 * Generates a preview certificate with sample data
 */
export function generatePreviewCertificate(templateHtml: string): string {
  const sampleData: CertificateData = {
    studentName: 'John Doe',
    courseName: 'Florida 4-Hour Basic Driver Improvement (BDI) Course',
    completionDate: new Date(),
    certificateNumber: 'DC-BDI-' + new Date().getFullYear() + '-12345',
    citationNumber: 'FL12345',
    county: 'Miami-Dade',
    driverLicense: 'A123-456-78-901-2',
    dob: new Date(1990, 0, 15), // January 15, 1990
    courseReason: 'Court-Ordered',
  };
  
  return generateCertificateHtml(templateHtml, sampleData);
}

/**
 * Interface for a certificate import batch
 */
export interface CertificateImportBatch {
  id: string;
  imported_by: string;
  state: string;
  course_type: string;
  total_imported: number;
  created_at: string;
  file_name?: string;
  notes?: string;
}

/**
 * Interface for a certificate number inventory item
 */
export interface CertificateNumberInventory {
  id: string;
  certificate_number: string;
  state: string;
  course_type: string;
  is_used: boolean;
  used_by?: number;
  assigned_at?: string;
  created_at: string;
  import_batch_id?: string;
}

/**
 * Formats a certificate number for display
 */
export function formatCertificateNumber(certificateNumber: string): string {
  return certificateNumber.trim();
}

/**
 * Parses a batch of certificate numbers from text input
 * @param input Raw text input containing certificate numbers
 * @param state State code for the certificates
 * @param courseType Course type code
 * @returns Array of parsed certificate numbers
 */
export function parseCertificateNumbers(
  input: string, 
  state: string, 
  courseType: string
): { certificateNumber: string, state: string, courseType: string }[] {
  // Split by newlines, commas, or spaces and process each line
  return input
    .split(/[\n,\s]+/)
    .map(line => line.trim())
    .filter(line => line.length > 0) // Remove empty lines
    .map(certificateNumber => ({
      certificateNumber,
      state,
      courseType
    }));
}

/**
 * Gets the next available certificate number from inventory
 * @param state State code
 * @param courseType Course type code
 * @returns The next available certificate number or undefined if none available
 */
export async function getNextAvailableCertificateNumber(
  state: string,
  courseType: string
): Promise<string | undefined> {
  try {
    // Call the Supabase function to get next available number
    const { data, error } = await supabase
      .rpc('get_next_available_certificate_number', {
        p_state: state,
        p_course_type: courseType,
      });
    
    if (error) throw error;
    return data;
  } catch (error) {
    console.error("Error getting next available certificate number:", error);
    return undefined;
  }
}

/**
 * Marks a certificate number as used in inventory
 * @param certificateNumber The certificate number to mark as used
 * @param certificateId The ID of the certificate that used this number
 * @returns True if successful, false otherwise
 */
export async function markCertificateNumberAsUsed(
  certificateNumber: string,
  certificateId: number
): Promise<boolean> {
  try {
    const { error } = await supabase
      .from('certificate_number_inventory')
      .update({
        is_used: true,
        used_by: certificateId,
        assigned_at: new Date().toISOString()
      })
      .eq('certificate_number', certificateNumber);
    
    if (error) throw error;
    return true;
  } catch (error) {
    console.error("Error marking certificate number as used:", error);
    return false;
  }
}

/**
 * Checks if a certificate number exists in inventory
 * @param certificateNumber The certificate number to check
 * @returns True if it exists, false otherwise
 */
export async function certificateNumberExists(certificateNumber: string): Promise<boolean> {
  try {
    const { data, error } = await supabase
      .from('certificate_number_inventory')
      .select('id')
      .eq('certificate_number', certificateNumber)
      .maybeSingle();
    
    if (error) throw error;
    return !!data;
  } catch (error) {
    console.error("Error checking if certificate number exists:", error);
    return false;
  }
}

/**
 * Gets the count of available certificate numbers
 * @param state Optional state filter
 * @param courseType Optional course type filter
 * @returns The count of available numbers
 */
export async function getAvailableCertificateCount(
  state?: string,
  courseType?: string
): Promise<number> {
  try {
    let query = supabase
      .from('certificate_number_inventory')
      .select('*', { count: 'exact' })
      .eq('is_used', false);
    
    if (state) {
      query = query.eq('state', state);
    }
    
    if (courseType) {
      query = query.eq('course_type', courseType);
    }
    
    const { count, error } = await query;
    
    if (error) throw error;
    return count || 0;
  } catch (error) {
    console.error("Error getting available certificate count:", error);
    return 0;
  }
}
